import React, { useEffect, useState } from "react";
import { TextInput } from "../components/form/inputs";
import DecorativeSquares from "../components/ui/DecorativeSquares";
import { useNavigate, useParams } from "react-router-dom";
import InputBase from "../components/form/inputs/InputBase";
import PrimaryBtn from "../components/button/PrimaryBtn";
import { GoogleMap, Marker, Autocomplete } from "@react-google-maps/api";
import { useDispatch, useSelector } from "react-redux";
import {
  addOrupdateAddress,
  getCustomerAddressById,
} from "../redux/thunks/customerAddThunk";
import toast from "react-hot-toast";
import { FaArrowLeft } from "react-icons/fa";
import { decryptId } from "../utils/urlCrypto";

const extractAddress = (place) => {
  const address = {
    country: "",
    city: "",
    province: "",
    pincode: "",
    fullAddress: place.formatted_address || "",
    addressLine2: "",
  };

  let sublocality = "";
  let locality = "";
  let route = "";

  place.address_components.forEach((component) => {
    const types = component.types;

    if (types.includes("country")) address.country = component.long_name;
    if (types.includes("administrative_area_level_1"))
      address.province = component.long_name;
    if (types.includes("locality")) {
      address.city = component.long_name;
      locality = component.long_name;
    }
    if (types.includes("sublocality") || types.includes("sublocality_level_1"))
      sublocality = component.long_name;
    if (types.includes("route")) route = component.long_name;
    if (types.includes("postal_code")) address.pincode = component.long_name;
  });

  // 🔥 Short nearby address (priority wise)
  address.addressLine2 =
    sublocality || route || locality || address.province || "";

  return address;
};

const EditAddress = () => {
  const dispatch = useDispatch();
  const { address_id: paramAddressId } = useParams();
  const address_id = decryptId(paramAddressId);
  const navigate = useNavigate();
  const handleGoBack = () => {
    navigate(-1);
  };
  const [address, setAddress] = useState({
    fullAddress: "",
    addressLine2: "",
    country: "",
    city: "",
    province: "",
    pincode: "",
    lat: null,
    lng: null,
  });

  const mapContainerStyle = {
    width: "100%",
    height: "300px",
  };

  const center = {
    lat: 20.5937,
    lng: 78.9629,
  };

  const [mapCenter, setMapCenter] = useState(center);
  const [markerPosition, setMarkerPosition] = useState(null);
  const autocompleteRef = React.useRef(null);
  const [customerId, setCustomerId] = useState(null);
  const [isFetchingAddress, setIsFetchingAddress] = useState(false);
  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (!place.geometry) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    const extracted = extractAddress(place);

    setAddress((prev) => ({
      ...prev,
      ...extracted,
      lat,
      lng,
    }));

    setMapCenter({ lat, lng });
    setMarkerPosition({ lat, lng });
  };
  const handleMapClick = (event) => {
    setIsFetchingAddress(true);
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    setMarkerPosition({ lat, lng });
    setMapCenter({ lat, lng });

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        const place = results[0];

        const extracted = extractAddress(place);

        setAddress((prev) => ({
          ...prev,
          ...extracted,
          lat,
          lng,
        }));
      } else {
        toast.error("Unable to fetch address from map");
      }

      setTimeout(() => {
        setIsFetchingAddress(false);
      }, 400);
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddress((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    const payload = { address_id };
    dispatch(getCustomerAddressById(payload))
      .unwrap()
      .then((res) => {
        const data = res.data;

        // Set the fetched address in state
        setAddress({
          fullAddress: data.address_1 || "",
          addressLine2: data.address_2 || "",
          country: data.country || "",
          city: data.city || "",
          province: data.province || "",
          pincode: data.pin_code || "",
        });
        setCustomerId(data.customer_id);

        // Map
        if (data?.latitude && data?.longitude) {
          const latLng = {
            lat: Number(data.latitude),
            lng: Number(data.longitude),
          };

          setMapCenter(latLng);
          setMarkerPosition(latLng);
        }
      })
      .catch((err) => {
        toast.error("Failed to fetch address details");
        console.error(err);
      });
  }, [address_id, dispatch]);

  const onAddressUpdate = async () => {
    const rebuiltAddress = [
      address.addressLine2,
      address.city,
      address.province,
      address.pincode,
      address.country,
    ]
      .filter(Boolean)
      .join(", ");
    try {
      const payload = {
        address_id: address_id,
        customer_id: customerId,
        address_1: rebuiltAddress,
        address_2: address.addressLine2,
        country: address.country,
        city: address.city,
        province: address.province,
        pin_code: address.pincode,
        latitude: address.lat,
        longitude: address.lng,
      };
      // console.log("The payload data is :", payload);

      const res = await dispatch(addOrupdateAddress(payload)).unwrap();

      if (res.success) {
        toast.success(res.message || "Address Added Successfully");
        navigate(`/address/${customerId}`);
      } else {
        toast.error(res.message || "Address Failed");
      }
    } catch (err) {
      toast.error(err?.message || err);
    }
  };

  return (
    <section className="relative min-h-screen  max-w-7xl mx-auto bg-slate-50 space-y-10">
      <DecorativeSquares />

      {/* Header */}
      <div className="sticky top-2 z-10 bg-white/80 backdrop-blur-md rounded-2xl shadow-md px-4 py-3 flex items-center justify-between mb-6">
        <button
          onClick={handleGoBack}
          className="flex items-center p-2 rounded-full hover:bg-gray-100"
        >
          <FaArrowLeft size={20} />
        </button>

        <h1 className="mx-auto text-2xl font-semibold text-gray-900">
          Edit Address
        </h1>
      </div>
      <div className="px-4 mt-8">
        <div className="mx-auto space-y-4 w-full max-w-7xl bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="w-full mt-4">
            <p className="text-[#191919] font-medium">Delivery Address</p>

            <Autocomplete
              onLoad={(ref) => (autocompleteRef.current = ref)}
              onPlaceChanged={onPlaceChanged}
            >
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search location"
                  name="fullAddress"
                  value={address.fullAddress}
                  onChange={handleChange}
                  disabled={isFetchingAddress}
                  className="w-full px-4 py-2 mb-3 border rounded-lg outline-none pr-10"
                />

                {isFetchingAddress && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-black rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
            </Autocomplete>
          </div>

          {/* ================= Address Form ================= */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputBase
              type="textarea"
              label="Address line 2"
              placeholder="Apartment, House No, Landmark"
              name="addressLine2"
              value={address.addressLine2}
              onChange={handleChange}
              rows={2}
            />

            <TextInput
              label="Country Name"
              placeholder="Enter Country Name"
              name="country"
              value={address.country}
              onChange={handleChange}
            />

            <TextInput
              label="City"
              placeholder="Enter City"
              name="city"
              value={address.city}
              onChange={handleChange}
            />

            <TextInput
              label="Province / State"
              placeholder="Enter Province"
              name="province"
              value={address.province}
              onChange={handleChange}
            />

            <TextInput
              label="Pin Code"
              placeholder="Enter Pin Code"
              name="pincode"
              value={address.pincode}
              onChange={handleChange}
            />
          </div>
          {/* ================= Google Map ================= */}
          <div className="space-y-2">
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              zoom={15}
              center={mapCenter}
              onClick={handleMapClick}
            >
              {markerPosition && <Marker position={markerPosition} />}
            </GoogleMap>
            {address.city && (
              <p className="text-sm text-gray-600 mt-1">
                📍 {address.city}
                {address.state ? `, ${address.state}` : ""}
                {address.country ? `, ${address.country}` : ""}
              </p>
            )}
          </div>
          <div>
            <PrimaryBtn btnName="Update" clickEvent={onAddressUpdate} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default EditAddress;
